import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetAllDocumentsList,
  useGetAssessmentDocuments,
  useGetDocumentDetails,
  useGetProviderDocuments,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../custom-hooks/queries";
import ProviderDetailCard from "../components/ProviderDetailCard";
import CustomCards from "../components/CustomCards";
import Drawer from "../components/Drawer";
import FIltersDropdown from "../components/Filters/FiltersDropdown";
import ArchiveStatusFilter, {
  ArchiveStatusFilterPanel,
} from "../components/Filters/ArchiveStatusFilter";
import DocumentLocationFilter from "../components/Filters/DocumentLocationFilter";
import LocationFiltersEditor from "../components/Filters/LocationFiltersEditor";
import {
  DEFAULT_LOCATION_FILTERS,
  DOCUMENT_LIBRARY_MAX_LOCATION_ROWS,
  LOCATION_FILTER_DEBOUNCE_MS,
} from "../utils/locationFilterUtils";
import {
  buildDocumentLibraryServiceNames,
  hasExtraDocumentLibraryFilters,
  shouldFlushLocationFiltersImmediately,
  shouldShowDocumentLibraryLoading,
  toStableLocationFilterParams,
} from "../utils/documentLibraryFilters";
import Pagination from "../components/CustomPagination";
import CustomCardsSkeleton from "../components/CustomCardsSkeleton";
import MainSearchBar from "../components/MainSearchbar";
import { useScreenWidth } from "../custom-hooks/useScreenWidth";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  setSearchTextByPage,
} from "../redux-store/features/providerFiltersSlice";
import Notification from "../components/Notification";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { useUser } from "@clerk/clerk-react";
import { isBackendViewOnlyUser, useUserRole, getUserType, isDiligentAdminUserRole } from "../utils/helperMethods";
import { getLocationFromProvider } from "../utils/addressUtils";
import DocumentLibrarySortBar from "../components/Filters/DocumentLibrarySortBar";
import {
  sortConfigToDocumentLibraryApiParams,
} from "../utils/documentLibrarySortMapper";
import useDeferredDocumentLibraryRefresh from "../custom-hooks/useDeferredDocumentLibraryRefresh";
import usePersistDocumentLibraryFilters from "../custom-hooks/usePersistDocumentLibraryFilters";
import {
  DOCUMENT_LIBRARY_SCREEN_KEYS,
  loadDocumentLibraryFilterState,
} from "../utils/documentLibraryFilterPersistence";

export const DocumentLibrary = () => {
  const initialFilterState = useMemo(
    () =>
      loadDocumentLibraryFilterState(
        DOCUMENT_LIBRARY_SCREEN_KEYS.DOCUMENT_LIBRARY,
        { includeLocation: true, includeArchive: true }
      ),
    []
  );
  const [currentPage, setCurrentPage] = useState(initialFilterState.currentPage);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const screenWidth = useScreenWidth();
  const location = useLocation();
  const userRole = useUserRole();
  const { user } = useUser();
  const hideRequestActions = isBackendViewOnlyUser(userRole);
  const isSponsor = getUserType(userRole) === "sponsor";
  const isCatalogAdmin = isDiligentAdminUserRole(userRole);
  const [archiveStatusFilter, setArchiveStatusFilter] = useState(
    initialFilterState.archiveStatusFilter
  );
  const [locationFilters, setLocationFilters] = useState(
    initialFilterState.locationFilters
  );
  const [appliedLocationFilters, setAppliedLocationFilters] = useState(
    initialFilterState.appliedLocationFilters
  );
  const { data: providers } = useGetprovidersListForFilters({ hideDiscoveryStatus: true });
  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["documentLibrary"]
    ) || "";
  const { data: rfis } = UseGetRfiListForFilters();

  const [docId, setDocId] = useState("");
  const [selectedDocumentItem, setSelectedDocumentItem] = useState(null);

  const filters = [
    {
      id: "docType",
      name: "Document Type",
      options: [
        { docId: "RFI", label: "VQQ", checked: false },
        { docId: "VQA", label: "Audit Report", checked: false },
      ],
      total: 2,
    },
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },
    {
      id: "status",
      name: "Status",
      options: [
        { statusID: "Released", label: "Released", checked: false },
        { statusID: "Pending", label: "Pending", checked: false },
        { statusID: "Published", label: "Published", checked: false },
        { statusID: "Cancelled", label: "Cancelled", checked: false },
        { statusID: "Upcoming", label: "Upcoming", checked: false },
      ],
      total: 5,
    },
    {
      id: "services",
      name: "Services",
      options: rfis,
      total: rfis?.length ?? 0,
    },
  ];

  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "documentLibrary",
          searchText: searchQuery,
        })
      );
    }, 300),
    [dispatch]
  );

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);
  const scheduleLibraryRefresh = useDeferredDocumentLibraryRefresh(isDrawerOpen);

  const [selectedIds, setSelectedIds] = useState(initialFilterState.selectedIds);
  const [sortConfig, setSortConfig] = useState(initialFilterState.sortConfig);
  const [sortFetchNonce, setSortFetchNonce] = useState(0);
  const serverSortParams = useMemo(
    () => sortConfigToDocumentLibraryApiParams(sortConfig),
    [sortConfig]
  );

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const persistedFilterState = useMemo(
    () => ({
      selectedIds,
      sortConfig,
      currentPage,
      locationFilters,
      appliedLocationFilters,
      archiveStatusFilter,
    }),
    [
      selectedIds,
      sortConfig,
      currentPage,
      locationFilters,
      appliedLocationFilters,
      archiveStatusFilter,
    ]
  );

  usePersistDocumentLibraryFilters(
    DOCUMENT_LIBRARY_SCREEN_KEYS.DOCUMENT_LIBRARY,
    persistedFilterState
  );

  /** Keep page in sync with filter changes in the same turn (avoid page/filter race). */
  const handleSelectedIdsChange = useCallback(
    (nextSelectedIds) => {
      setSelectedIds(nextSelectedIds);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const handleArchiveStatusChange = useCallback(
    (nextStatus) => {
      setArchiveStatusFilter(nextStatus);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    setSortFetchNonce((n) => n + 1);
    resetToFirstPage();
  };

  const locationFilterParams = useMemo(
    () => toStableLocationFilterParams(appliedLocationFilters),
    [appliedLocationFilters]
  );

  const debouncedApplyLocationFilters = useCallback(
    debounce((filters) => {
      setAppliedLocationFilters(filters);
    }, LOCATION_FILTER_DEBOUNCE_MS),
    []
  );

  const clearDocLibraryExtraFilters = useCallback(() => {
    debouncedApplyLocationFilters.cancel();
    setLocationFilters(DEFAULT_LOCATION_FILTERS);
    setAppliedLocationFilters(DEFAULT_LOCATION_FILTERS);
    setArchiveStatusFilter("active_only");
    resetToFirstPage();
  }, [debouncedApplyLocationFilters, resetToFirstPage]);

  const hasExtraDocLibraryFilters = hasExtraDocumentLibraryFilters({
    locationFilters,
    archiveStatusFilter,
    isCatalogAdmin,
  });

  const docLibraryExtraMobileSections = useMemo(() => {
    const sections = [
      {
        id: "location",
        name: "Location",
        content: (
          <LocationFiltersEditor
            locationFilters={locationFilters}
            setLocationFilters={setLocationFilters}
            layout="vertical"
            allowMultiple={DOCUMENT_LIBRARY_MAX_LOCATION_ROWS > 1}
          />
        ),
      },
    ];

    if (isCatalogAdmin) {
      sections.push({
        id: "archiveStatus",
        name: "Archive Status",
        content: (
          <ArchiveStatusFilterPanel
            value={archiveStatusFilter}
            onChange={handleArchiveStatusChange}
            idPrefix="mobile-archive-status"
          />
        ),
      });
    }

    return sections;
  }, [
    locationFilters,
    archiveStatusFilter,
    isCatalogAdmin,
    handleArchiveStatusChange,
  ]);

  const docLibraryExtraDesktopFilters = (
    <>
      <DocumentLocationFilter
        locationFilters={locationFilters}
        setLocationFilters={setLocationFilters}
      />
      {isCatalogAdmin ? (
        <ArchiveStatusFilter
          value={archiveStatusFilter}
          onChange={handleArchiveStatusChange}
        />
      ) : null}
    </>
  );

  useEffect(() => {
    if (
      shouldFlushLocationFiltersImmediately(
        locationFilters,
        appliedLocationFilters
      )
    ) {
      debouncedApplyLocationFilters.cancel();
      setAppliedLocationFilters(locationFilters);
      resetToFirstPage();
      return undefined;
    }

    debouncedApplyLocationFilters(locationFilters);
    return () => debouncedApplyLocationFilters.cancel();
  }, [
    locationFilters,
    appliedLocationFilters,
    debouncedApplyLocationFilters,
    resetToFirstPage,
  ]);

  const skipPageResetOnMount = useRef(true);

  useEffect(() => {
    if (skipPageResetOnMount.current) {
      skipPageResetOnMount.current = false;
      return;
    }

    resetToFirstPage();
  }, [searchQuery, locationFilterParams, resetToFirstPage]);

  // For Documents page, pass service names instead of IDs
  const serviceNames = useMemo(
    () => buildDocumentLibraryServiceNames(selectedIds.services, rfis),
    [selectedIds.services, rfis]
  );
  const rfiIds = useMemo(() => [], []);

  // filter documents by discovery status
  const discoveryStatus = true;
  const {
    data,
    isFetching,
    isPlaceholderData,
    isError,
    error,
    refetch: libraryRefetech,
  } = useGetAllDocumentsList(
    currentPage,
    searchQuery,
    selectedIds.docType,
    selectedIds.provider,
    selectedIds.status,
    rfiIds, // Empty array for Documents page
    undefined, // vqaId
    undefined, // entityId
    serviceNames, // Pass service names
    discoveryStatus,
    isCatalogAdmin ? archiveStatusFilter : null,
    serverSortParams,
    locationFilterParams,
    sortFetchNonce,
  );
  const showLibraryLoading = shouldShowDocumentLibraryLoading({
    isFetching,
    isPlaceholderData,
  });
  const {
    data: drawerData,
    isPending: drawerLoading,
    isSuccess: drawerIsSuccess,
    refetch,
  } = useGetDocumentDetails(docId);

  useEffect(() => {
    if (isError) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [isError]);

  const advanceSearchHandler = () => {
    navigate("/Poc");
  };
  const templateUsageCount = {}; // Track how many times we've used each template_id

  const getDocLocation = (item, short = true) => {
    return getLocationFromProvider({
      address: item?.document_address || item?.document_location,
      city: item?.document_city,
      state: item?.document_state,
      country: item?.document_country
    }, { short, abbvState: true });
  };

  return (
    <div className="container px-4">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5 ">
        <h1 className="font-semibold">Document Library</h1>
        <p className="text-sm">
          The below displays a comprehensive list of all Vendor Qualification
          Questionnaire (VQQ) and Vendor Qualification Assessment (VQA) reports
          in the Diligent document library.
        </p>
      </div>
      <div className="items-center mt-5 px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="w-full min-w-0 shrink-0 lg:w-auto">
            <MainSearchBar
              pageKey="documentLibrary"
              placeholder="Search documents..."
              height="40px"
              width={
                screenWidth <= 375
                  ? "17.5rem"
                  : screenWidth < 425
                  ? "22rem"
                  : screenWidth < 1024
                  ? "100%"
                  : "25rem"
              }
              marginRight={"mr-2"}
              isSearchIcon={true}
              onSearch={onSubmit}
            />
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center gap-x-0 gap-y-2 lg:w-auto lg:justify-end">
            <DocumentLibrarySortBar
              sortConfig={sortConfig}
              onSort={handleSort}
              compact
            />
            <span
              className="mx-2 hidden h-5 w-px shrink-0 bg-gray-200 sm:block"
              aria-hidden="true"
            />
            <FIltersDropdown
              rfis={rfis}
              providers={providers}
              setSelectedIds={handleSelectedIdsChange}
              selectedIds={selectedIds}
              filters={filters}
              showGroupLabel
              useLargeScreenControls
              extraDesktopFilters={docLibraryExtraDesktopFilters}
              extraMobileSections={docLibraryExtraMobileSections}
              hasExtraFiltersActive={hasExtraDocLibraryFilters}
              onClearExtraFilters={clearDocLibraryExtraFilters}
            />
          </div>
        </div>
      </div>
      {showLibraryLoading ? (
        <CustomCardsSkeleton isProviderDetail={true} />
      ) : (
        <>
          {data?.items.length === 0 ? (
            <div
              className="flex flex-col items-center  justify-center text-center space-y-2 min-h-96"
              style={{
                marginTop: "100px",
              }}
            >
              <span className="p-3 rounded-full bg-custom-link-bg-color">
                <MagnifyingGlassIcon
                  width={20}
                  height={20}
                  style={{ color: "white" }}
                />
              </span>
              <p className="text-lg text-gray-700 font-semibold">
                {"No documents found"}
              </p>
              <p className="text-gray-500">
                {"Your search did not match any documents. Please try again."}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center lg:justify-start 2xl:justify-start items-start mt-5 min-h-96">
              {data?.items.map((item, index) => {
                // const templateId = item?.template_id;

                // If template_id is null, use document_status
                // if (templateId === null) {
                return (
                  <div key={item?.document_id} className="mb-4 md:ml-4">
                    <CustomCards
                      documentType={item?.entity_type}
                      name={item?.provider_name}
                      status={item?.document_status}
                      services={item?.capabilities}
                      additionalScope={item?.entity_type === "VQA" ? item?.additional_scope : undefined}
                      publishedDate={item?.published_on}
                      assessmentDate={item?.assessment_date}
                      requestedDate={item?.requested_on}
                      docLocation={getDocLocation(item)}
                      docLocationFull={getDocLocation(item, false)}
                      documentName={item?.document_name}
                      entityId={item?.entity_id}
                      docId={item?.document_id}
                      details={item}
                      questionnaireId={item?.hc_questionnaire_id}
                      isLegacyRfi={item?.is_legacy}
                      v1_doc_id={item?.v1_mongo_id}
                      v1_doc_name={item?.v1_document_name}
                      hideRequestActions={hideRequestActions}
                      libraryRefetch={libraryRefetech}
                      onClick={() => {
                        if (isSponsor && item?.entity_type === "RFI" && item?.document_status === "Published") {
                          const params = new URLSearchParams();
                          if (item?.provider_id) params.set("id", item.provider_id);
                          if (item?.template_id) params.set("templateId", item.template_id);
                          if (item?.document_name) params.set("templateName", item.document_name);
                          if (item?.document_id) params.set("documentId", item.document_id);
                          navigate(`/RFI/Create?${params.toString()}`);
                          return;
                        }
                        setDocId(item?.document_id);
                        setSelectedDocumentItem(item);
                        dispatch(setDrawer(true));
                      }}
                    />
                  </div>
                );

                // }

                // Get all RFIs that match the current template_id
                // const matchedRfis =
                //   rfisData?.items?.filter(
                //     (rfi) => rfi?.templateId === templateId
                //   ) || [];

                // Track how many times this template_id has been used
                // const usageCount = templateUsageCount[templateId] || 0;

                // Pick the corresponding RFI based on usage count
                // const matchedRfi = matchedRfis[usageCount];

                // console.log(matchedRfi);

                // Update the counter for next use
                // templateUsageCount[templateId] = usageCount + 1;

                // const status = matchedRfi?.status ?? item?.document_status;

                // return (
                //   <div key={item?.document_id} className="mb-4 md:ml-4">
                //     <CustomCards
                //       documentType={item?.entity_type}
                //       name={item?.provider_name}
                //       status={status}
                //       services={item?.capabilities}
                //       publishedDate={item?.published_on}
                //       requestedDate={item?.requested_on}
                //       docLocation={getDocLocation(item)}
                //       documentName={item?.document_name}
                //       entityId={item?.entity_id}
                //       docId={item?.document_id}
                //       onClick={() => {
                //         setDocId(item?.document_id);
                //         dispatch(setDrawer(true));
                //       }}
                //       questionnaireId={matchedRfi?.questionnaire_id}
                //       sponsorRfiResponseId={matchedRfi?.sponsorRfiResponseId}
                //     />
                //   </div>
                // );
              })}
            </div>
          )}
        </>
      )}

      {/* Drawer Component */}
      {isDrawerOpen && (
        <Drawer
          open={isDrawerOpen}
          docId={drawerData?.document_id}
          docType={drawerData?.document_type}
          docName={drawerData?.document_name}
          documents={drawerData}
          fallbackDocument={selectedDocumentItem}
          drawerLoading={drawerLoading}
          refetch={refetch}
          scheduleLibraryRefresh={scheduleLibraryRefresh}
        />
      )}

      {!showLibraryLoading && data?.items.length > 0 && !isError && (
        <Pagination
          totalPages={data?.total_pages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error?.message}
        status={"error"}
      />
    </div>
  );
};
